// Phase 4 — Settings panels for the daily backup destinations and the
// six-sheet Excel hub. Both panels are admin-only (the parent SettingsView
// is already gated on isAdmin at the section level; these guard again
// internally for defence-in-depth).

import { useEffect, useRef, useState } from "react";
import { Copy, RefreshCw, Trash2, X, Download, Upload } from "lucide-react";
import { authHeaders } from "../api";
import { srvErrText } from "../lib/srvErr";
import { useTranslation } from "../i18n";
import { RAD, FS } from "../lib/tokens";
import { ModalLayout } from "./SharedUI";
import type { Translate } from "../lib/interestRefusal"; // 212 §18 — the report's words, typed at the seam
import type { ToastFn } from "./Toast";
/** 212 §18 — one address the settings import refused or could not check: its sheet, its row, the address. */
interface FolderNote { sheet?: string; row?: number; address?: string; reason?: string }

const fmtDate = (iso: any) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch (_e) {
    return iso;
  }
};

async function apiCall(path: any, opts: any = {}) {
  // 174 §1.2/§1.3 — the same three headers api() sends, from the one place
  // that defines them. The comment that stood here said a Bearer is sent only
  // "if one exists (none in the browser)": the browser holds BOTH credentials
  // and always has — api.ts:113 has read lx:token the whole time — and that
  // sentence is what produced §1's sixteen cookie-only call sites.
  const headers = { ...authHeaders(), ...(opts.headers || {}) };
  if (
    opts.body &&
    !(opts.body instanceof FormData) &&
    !headers["Content-Type"]
  ) {
    headers["Content-Type"] = "application/json";
  }
  const r = await fetch(path, { credentials: "same-origin", ...opts, headers });
  let body = null;
  try {
    body = await r.json();
  } catch (_e) {
    /* not json */
  }
  if (!r.ok) {
    // brief 38 §3 — third door of the same seam: this panel set has its own
    // fetch wrapper and already reads `code`, so the `srvErr.<code>` lookup
    // belongs here for the same reason it belongs in api.ts.
    const err: any = new Error(
      srvErrText(body) || body?.error || `HTTP ${r.status}`,
    );
    err.code = body?.code;
    err.invalid = body?.invalid;
    err.status = r.status;
    throw err;
  }
  return body;
}

// ============================================================================
// BackupDestinationsPanel
// ============================================================================

export function BackupDestinationsPanel({ toast }: { toast?: any }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [destinations, setDestinations] = useState<any[]>([]);
  const [sshKeyDir, setSshKeyDir] = useState("");
  const [form, setForm] = useState({
    nickname: "",
    tailscaleHost: "",
    sshUser: "",
    targetPath: "",
  });
  const [busy, setBusy] = useState(false);
  const [pubKeyDlg, setPubKeyDlg] = useState<any>(null);
  /* 214 §8 [RULED 54] — the destination row read ONCE typed (the rows are `any`): the limited line the window
     gives, falling back to the bare key only when the box could not say its request (and the window says so). */
  const dto = (row: unknown) => row as { authorizedKeysLine?: string | null; publicKey?: string | null; targetPath?: string | null } | null;
  const lineOf = (row: unknown) => {
    const r = dto(row);
    return r?.authorizedKeysLine ?? r?.publicKey ?? "";
  };
  const dlg = dto(pubKeyDlg);
  const [confirmDel, setConfirmDel] = useState<any>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await apiCall("/api/backup-destinations");
      setDestinations(r.destinations || []);
      setSshKeyDir(r.sshKeyDir || "");
    } catch (e: any) {
      toast?.("⚠ " + e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const add = async () => {
    if (
      !form.nickname.trim() ||
      !form.tailscaleHost.trim() ||
      !form.sshUser.trim() ||
      !form.targetPath.trim()
    ) {
      toast?.(
        t("phase4.backup.toast.allFieldsRequired", {
          fallback: "⚠ All fields are required",
        }),
      );
      return;
    }
    setBusy(true);
    try {
      const r = await apiCall("/api/backup-destinations", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setForm({ nickname: "", tailscaleHost: "", sshUser: "", targetPath: "" });
      await refresh();
      setPubKeyDlg(r.destination);
      toast?.(
        t("phase4.backup.toast.added", { fallback: "Destination added" }),
      );
    } catch (e: any) {
      const detail = e.invalid?.join("; ") || e.message;
      toast?.("⚠ " + detail);
    }
    setBusy(false);
  };

  const toggle = async (row: any) => {
    setBusy(true);
    try {
      await apiCall(`/api/backup-destinations/${row.id}`, {
        method: "PUT",
        body: JSON.stringify({ enabled: !row.enabled }),
      });
      await refresh();
    } catch (e: any) {
      toast?.("⚠ " + e.message);
    }
    setBusy(false);
  };

  const remove = async (row: any) => {
    setBusy(true);
    try {
      await apiCall(`/api/backup-destinations/${row.id}`, { method: "DELETE" });
      setConfirmDel(null);
      await refresh();
      toast?.(
        t("phase4.backup.toast.deleted", { fallback: "Destination deleted" }),
      );
    } catch (e: any) {
      toast?.("⚠ " + e.message);
    }
    setBusy(false);
  };

  const copyPub = (pub: any) => {
    if (!pub) return;
    try {
      navigator.clipboard.writeText(pub);
      toast?.(
        t("phase4.backup.toast.pubKeyCopied", {
          fallback: "Public key copied",
        }),
      );
    } catch {
      toast?.(
        t("phase4.backup.toast.clipboardFailed", {
          fallback: "⚠ Clipboard access denied",
        }),
      );
    }
  };

  return (
    <>
      <p
        style={{
          fontSize: FS.s11_5,
          color: "var(--ts)",
          margin: "6px 0 10px",
          lineHeight: 1.6,
        }}
      >
        {t("phase4.backup.desc", {
          fallback:
            "Remote machines that receive the nightly pg_dump push over Tailscale. The server generates a per-row ed25519 keypair when you add a destination — copy the public key to the destination host's authorized_keys.",
        })}
      </p>
      <p
        style={{
          fontSize: FS.s10,
          color: "var(--tm)",
          margin: "0 0 12px",
          lineHeight: 1.5,
        }}
      >
        {t("phase4.backup.keyDir", { fallback: "SSH key directory:" })}{" "}
        <span style={{ fontFamily: "'JetBrains Mono',monospace" }}>
          {sshKeyDir || "—"}
        </span>
      </p>

      {loading ? (
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {t("common.loading", { fallback: "Loading…" })}
        </p>
      ) : destinations.length === 0 ? (
        <p style={{ fontSize: FS.s11, color: "var(--tm)", fontStyle: "italic" }}>
          {t("phase4.backup.empty", {
            fallback: "No destinations configured yet.",
          })}
        </p>
      ) : (
        <div style={{ marginBottom: 12 }}>
          {destinations.map((d) => (
            <div
              key={d.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 8,
                alignItems: "center",
                padding: "8px 0",
                borderBottom: "1px solid rgba(255,255,255,.06)",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: FS.s11_5,
                    fontWeight: 600,
                    color: "var(--tp)",
                  }}
                >
                  {d.nickname}{" "}
                  <span
                    style={{
                      fontWeight: 400,
                      color: "var(--tm)",
                      fontSize: FS.s10_5,
                    }}
                  >
                    {d.sshUser}@{d.tailscaleHost}:{d.targetPath}
                  </span>
                </div>
                <div style={{ fontSize: FS.s10, color: "var(--tm)", marginTop: 2 }}>
                  {d.enabled ? (
                    <span style={{ color: "var(--gn)" }}>
                      {t("phase4.backup.status.enabled", {
                        fallback: "● enabled",
                      })}
                    </span>
                  ) : (
                    <span style={{ color: "var(--tm)" }}>
                      {t("phase4.backup.status.disabled", {
                        fallback: "○ disabled",
                      })}
                    </span>
                  )}
                  {d.lastPushedAt && (
                    <span style={{ marginLeft: 10 }}>
                      {t("phase4.backup.lastPushed", {
                        fallback: "last push:",
                      })}{" "}
                      {fmtDate(d.lastPushedAt)}
                    </span>
                  )}
                  {d.lastError && (
                    <span style={{ marginLeft: 10, color: "var(--rd)" }}>
                      {t("phase4.backup.lastError", { fallback: "error:" })}{" "}
                      {String(d.lastError).slice(0, 120)}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button
                  className="btn btn-ghost"
                  disabled={busy}
                  style={{ padding: "3px 8px", fontSize: FS.s10 }}
                  onClick={() => copyPub(lineOf(d))}
                  title={t("phase4.backup.btn.copyLine", {
                    fallback: "Copy the authorized_keys line",
                  })}
                >
                  <Copy size={11} />
                </button>
                <button
                  className="btn btn-ghost"
                  disabled={busy}
                  style={{ padding: "3px 8px", fontSize: FS.s10 }}
                  onClick={() => toggle(d)}
                  title={
                    d.enabled
                      ? t("phase4.backup.btn.disable", { fallback: "Disable" })
                      : t("phase4.backup.btn.enable", { fallback: "Enable" })
                  }
                >
                  {d.enabled
                    ? t("phase4.backup.btn.disable", { fallback: "Disable" })
                    : t("phase4.backup.btn.enable", { fallback: "Enable" })}
                </button>
                <button
                  className="btn btn-danger"
                  disabled={busy}
                  style={{ padding: "3px 8px", fontSize: FS.s10 }}
                  onClick={() => setConfirmDel(d)}
                  title={t("common.delete", { fallback: "Delete" })}
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
          <button
            className="btn btn-ghost"
            disabled={busy}
            style={{ padding: "3px 8px", fontSize: FS.s10, marginTop: 6 }}
            onClick={refresh}
          >
            <RefreshCw
              size={10}
              style={{ marginRight: 3, verticalAlign: "middle" }}
            />
            {t("common.refresh", { fallback: "Refresh" })}
          </button>
        </div>
      )}

      <div
        style={{
          borderTop: "1px solid var(--br)",
          paddingTop: 10,
          marginTop: 10,
        }}
      >
        <h5
          style={{
            fontSize: FS.s11,
            fontWeight: 600,
            margin: "0 0 8px",
            color: "var(--tp)",
          }}
        >
          {t("phase4.backup.addNew", { fallback: "Add new destination" })}
        </h5>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 6,
            marginBottom: 6,
          }}
        >
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              fontSize: FS.s10,
              color: "var(--tm)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {t("phase4.backup.label.nickname", { fallback: "Nickname" })}
            <input autoComplete="off" /* 207 §18 */
              className="field-input no-contact-autofill"
              style={{
                fontSize: FS.s11,
                padding: "3px 6px",
                fontWeight: 400,
                textTransform: "none",
                letterSpacing: 0,
              }}
              placeholder={t("phase4.backup.placeholder.nickname", {
                fallback: "Nickname (alphanumeric, _, -, .)",
              })}
              value={form.nickname}
              onChange={(e) =>
                setForm((f) => ({ ...f, nickname: e.target.value }))
              }
            />
          </label>
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              fontSize: FS.s10,
              color: "var(--tm)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {t("phase4.backup.label.tailscaleHost", {
              fallback: "Tailscale host",
            })}
            <input autoComplete="off" /* 207 §18 */
              className="field-input no-contact-autofill"
              style={{
                fontSize: FS.s11,
                padding: "3px 6px",
                fontWeight: 400,
                textTransform: "none",
                letterSpacing: 0,
              }}
              placeholder={t("phase4.backup.placeholder.tailscaleHost", {
                fallback: "Tailscale hostname (e.g. macstudio.tail-abc.ts.net)",
              })}
              value={form.tailscaleHost}
              onChange={(e) =>
                setForm((f) => ({ ...f, tailscaleHost: e.target.value }))
              }
            />
          </label>
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              fontSize: FS.s10,
              color: "var(--tm)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {t("phase4.backup.label.sshUser", { fallback: "SSH user" })}
            <input
              className="field-input"
              style={{
                fontSize: FS.s11,
                padding: "3px 6px",
                fontWeight: 400,
                textTransform: "none",
                letterSpacing: 0,
              }}
              placeholder={t("phase4.backup.placeholder.sshUser", {
                fallback: "SSH user",
              })}
              value={form.sshUser}
              onChange={(e) =>
                setForm((f) => ({ ...f, sshUser: e.target.value }))
              }
            />
          </label>
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              fontSize: FS.s10,
              color: "var(--tm)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {t("phase4.backup.label.targetPath", { fallback: "Target path" })}
            <input
              className="field-input"
              style={{
                fontSize: FS.s11,
                padding: "3px 6px",
                fontFamily: "'JetBrains Mono',monospace",
                fontWeight: 400,
                textTransform: "none",
                letterSpacing: 0,
              }}
              value={form.targetPath}
              onChange={(e) =>
                setForm((f) => ({ ...f, targetPath: e.target.value }))
              }
            />
          </label>
        </div>
        <button
          className="btn btn-primary"
          disabled={busy}
          style={{ padding: "3px 10px", fontSize: FS.s11 }}
          onClick={add}
        >
          {busy
            ? t("phase4.backup.btn.adding", { fallback: "Adding…" })
            : t("phase4.backup.btn.addAndKeygen", {
                fallback: "+ Add & generate keypair",
              })}
        </button>
      </div>

      {pubKeyDlg && (
        <ModalLayout
          title={
            <>
              {t("phase4.backup.pubKey.title", {
                fallback: "Public key for ",
              })}
              {pubKeyDlg.nickname}
            </>
          }
          onClose={() => setPubKeyDlg(null)}
          closeOnOverlay
          maxWidth={600}
          rawBody
          footer={
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                padding: "10px 20px",
                borderTop: "1px solid var(--br)",
              }}
            >
              <button
                className="btn btn-ghost"
                onClick={() => copyPub(lineOf(pubKeyDlg))}
              >
                <Copy
                  size={11}
                  style={{ marginRight: 3, verticalAlign: "middle" }}
                />
                {t("phase4.backup.btn.copyToClipboard", {
                  fallback: "Copy to clipboard",
                })}
              </button>
              <button className="btn btn-primary" onClick={() => setPubKeyDlg(null)}>
                {t("common.close", { fallback: "Close" })}
              </button>
            </div>
          }
        >
            <div style={{ padding: "14px 20px" }}>
              <p
                style={{
                  fontSize: FS.s11_5,
                  color: "var(--ts)",
                  margin: "0 0 10px",
                  lineHeight: 1.5,
                }}
              >
                {/* 214 §8 [RULED 54] — the window gives the LIMITED line and says what it allows: receiving files
                    into that folder by rsync, nothing else — no shell, no other folder; and that a change of this
                    box's rsync or of the folder means pasting the line again. The bare key is shown only when the
                    box could not say its request, and the window says so. */}
                {dlg?.authorizedKeysLine
                  ? t("phase4.backup.pubKey.line_body", {
                      path: dlg.targetPath ?? "",
                      fallback:
                        "Copy this line into ~/.ssh/authorized_keys on the destination host. It allows one thing: receiving the backup files into " +
                        (dlg.targetPath ?? "") +
                        " by rsync — no shell, no other folder, no other command. The private key stays on this server at the path shown below.",
                    })
                  : t("phase4.backup.pubKey.line_unavailable", {
                      fallback:
                        "This server could not say its rsync request, so the line below is the bare public key: it would let the key run any command on the destination. Fix rsync on this server before pasting anything.",
                    })}
              </p>
              <textarea
                readOnly
                data-authorized-keys-line={dlg?.authorizedKeysLine ? "1" : "0"}
                value={lineOf(pubKeyDlg)}
                style={{
                  width: "100%",
                  height: 72,
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: FS.s10_5,
                  padding: 6,
                  background: "var(--bi)",
                  color: "var(--tp)",
                  border: "1px solid var(--br)",
                  borderRadius: RAD.r4,
                  resize: "none",
                }}
              />
              <p
                style={{
                  fontSize: FS.s10,
                  color: "var(--tm)",
                  margin: "8px 0 0",
                  lineHeight: 1.5,
                }}
              >
                {t("phase4.backup.pubKey.privatePath", {
                  fallback: "Private key file (server-side, 0600):",
                })}{" "}
                <span style={{ fontFamily: "'JetBrains Mono',monospace" }}>
                  {pubKeyDlg.sshKeyPath}
                </span>
              </p>
              {dlg?.authorizedKeysLine && (
                <p style={{ fontSize: FS.s10, color: "var(--tm)", margin: "6px 0 0", lineHeight: 1.5 }}>
                  {t("phase4.backup.pubKey.line_repaste", {
                    fallback: "If rsync on this server changes, or the destination folder does, the request changes with it: open this window again and paste the new line.",
                  })}
                </p>
              )}
            </div>
        </ModalLayout>
      )}

      {confirmDel && (
        <ModalLayout
          title={t("phase4.backup.confirmDel.title", {
            fallback: "Delete destination?",
          })}
          onClose={() => setConfirmDel(null)}
          closeOnOverlay
          closeDisabled={busy}
          maxWidth={440}
          rawBody
          footer={
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                padding: "10px 20px",
                borderTop: "1px solid var(--br)",
              }}
            >
              <button
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => setConfirmDel(null)}
              >
                {t("common.cancel", { fallback: "Cancel" })}
              </button>
              <button
                className="btn btn-danger"
                disabled={busy}
                onClick={() => remove(confirmDel)}
              >
                {busy
                  ? t("phase4.backup.btn.deleting", { fallback: "Deleting…" })
                  : t("phase4.backup.btn.deleteConfirm", {
                      fallback: "Delete & shred key",
                    })}
              </button>
            </div>
          }
        >
            <div style={{ padding: "14px 20px" }}>
              <p
                style={{
                  fontSize: FS.s11_5,
                  color: "var(--ts)",
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                {t("phase4.backup.confirmDel.body", {
                  fallback:
                    "This will remove the destination row and shred the associated private key file on the server. The destination host's authorized_keys entry must be removed manually.",
                })}
              </p>
              <p
                style={{ fontSize: FS.s11, color: "var(--tm)", margin: "8px 0 0" }}
              >
                <b style={{ color: "var(--tp)" }}>{confirmDel.nickname}</b> —{" "}
                {confirmDel.sshUser}@{confirmDel.tailscaleHost}
              </p>
            </div>
        </ModalLayout>
      )}
    </>
  );
}

// ============================================================================
// SettingsXlsxHubPanel
// ============================================================================

export function SettingsXlsxHubPanel({ toast }: { toast?: any }) {
  const { t } = useTranslation();
  const fileRef = useRef<any>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<any>(null);

  const download = async () => {
    try {
      // 174 §1.2 — a BLOB response, which api() cannot carry, so the bare
      // fetch stays and spreads exactly what api() spreads. Both credentials.
      const r = await fetch("/api/settings-xlsx/export", {
        credentials: "same-origin",
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error("HTTP " + r.status);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lexcrm-settings-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast?.(
        t("phase4.xlsx.toast.downloaded", { fallback: "Workbook downloaded" }),
      );
    } catch (e: any) {
      toast?.("⚠ " + e.message);
    }
  };

  const startPreview = async (file: any) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const j = await apiCall("/api/settings-xlsx/preview", {
        method: "POST",
        body: fd,
      });
      setPreview({ file, diff: j });
    } catch (e: any) {
      const detail = e.invalid?.join("; ") || e.message;
      toast?.("⚠ " + detail);
    }
    setBusy(false);
  };

  const confirmApply = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", preview.file);
      const j = await apiCall("/api/settings-xlsx/apply", {
        method: "POST",
        body: fd,
      });
      const total = Object.values(j.summary || {}).reduce(
        (s: any, v: any) => s + (v.created || 0),
        0,
      );
      const errs = (j.errors || []).length;
      // typed at this one seam: `t` and `toast` reach this panel untyped
      const tt = t as Translate;
      const say = toast as ToastFn | undefined;
      const applied = tt("phase4.xlsx.toast.applied", {
        count: total,
        fallback:
          `Applied — ${String(total)} new row(s)` +
          (errs ? `, ${String(errs)} error(s)` : ""),
      });
      /* 212 §18 — THE STOP, IN THE IMPORT'S REPORT: a row whose folder address
         SharePoint has nothing at was stored WITHOUT it; one SharePoint could
         not be asked about was stored with it. Both are named — in the SAME
         toast as the import's count: the app shows one toast at a time and a
         second replaces the first before it is drawn (drive-212 --part=folders
         saw only the last of three), so three calls said one thing. */
      const folders = (j as { folders?: { refused?: FolderNote[]; unchecked?: FolderNote[] } }).folders;
      const cite = (xs: FolderNote[]) =>
        xs.slice(0, 5).map((x) => (x.sheet ?? "") + " " + String(x.row ?? "") + " — " + (x.address ?? "")).join(" ; ") +
        (xs.length > 5 ? " ; +" + String(xs.length - 5) : "");
      const notes: string[] = [];
      if (folders?.refused?.length) {
        notes.push(
          tt("phase4.xlsx.toast.folders_refused", {
            list: cite(folders.refused),
            fallback: "⚠ Folder addresses SharePoint has nothing at — the rows were saved without them: " + cite(folders.refused),
          }),
        );
      }
      if (folders?.unchecked?.length) {
        notes.push(
          tt("phase4.xlsx.toast.folders_unchecked", {
            list: cite(folders.unchecked),
            fallback: "⚠ Folder addresses that could not be checked in SharePoint — saved as typed: " + cite(folders.unchecked),
          }),
        );
      }
      if (notes.length) say?.([applied, ...notes.map((x) => x.replace(/^⚠\s*/, ""))].join(" · "), "warning");
      else say?.(applied);
      setPreview(null);
    } catch (e: any) {
      toast?.("⚠ " + e.message);
    }
    setBusy(false);
  };

  // §4.10: short-circuit Apply button when there's nothing to create.
  const totalCreates = preview
    ? Object.values(preview.diff || {}).reduce<number>(
        (s, v: any) => s + (v?.counts?.create || 0),
        0,
      )
    : 0;
  const anyFkInvalid = preview
    ? Object.values(preview.diff || {}).reduce<number>(
        (s, v: any) => s + (v?.counts?.fk_invalid || 0),
        0,
      )
    : 0;

  const renderSheetSection = (name: any, sec: any) => {
    if (!sec) return null;
    const c = sec.counts || {};
    return (
      <div style={{ marginBottom: 10 }}>
        <h4
          style={{
            margin: "0 0 4px",
            fontSize: FS.s12,
            fontWeight: 600,
            color: "var(--tp)",
          }}
        >
          {name}
        </h4>
        <div
          style={{
            display: "flex",
            gap: 12,
            fontSize: FS.s10_5,
            color: "var(--ts)",
            flexWrap: "wrap",
          }}
        >
          <span>
            <b style={{ color: "var(--gn)" }}>{c.create || 0}</b>{" "}
            {t("phase4.xlsx.col.create", { fallback: "create" })}
          </span>
          <span>
            <b style={{ color: "var(--tm)" }}>{c.skipped_existing || 0}</b>{" "}
            {t("phase4.xlsx.col.skipExisting", { fallback: "skip (existing)" })}
          </span>
          {(c.fk_invalid || 0) > 0 && (
            <span>
              <b style={{ color: "var(--rd)" }}>{c.fk_invalid}</b>{" "}
              {t("phase4.xlsx.col.fkInvalid", { fallback: "fk invalid" })}
            </span>
          )}
          <span style={{ color: "var(--tm)" }}>
            {t("phase4.xlsx.col.totalInUpload", {
              fallback: "total in upload:",
            })}{" "}
            {c.total_in_xlsx || 0}
          </span>
        </div>
        {sec.samples?.fk_invalid?.length > 0 && (
          <ul
            style={{
              margin: "4px 0 0",
              padding: "0 0 0 16px",
              fontSize: FS.s10,
              color: "var(--rd)",
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            {sec.samples.fk_invalid.slice(0, 5).map((s: any, i: any) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {t("phase4.xlsx.row_n", { n: s.row, reason: s.reason, fallback: "row {n}: {reason}" })}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <>
      <p
        style={{
          fontSize: FS.s11_5,
          color: "var(--ts)",
          margin: "6px 0 10px",
          lineHeight: 1.6,
        }}
      >
        {/* 134 §3/§5 — A NEW KEY, NOT AN EDITED ONE. The translation seed only
            INSERTS, so a changed string never reaches a database that already
            holds the old row: `phase4.xlsx.desc` would have kept saying
            "seven-sheet" on every instance already seeded. */}
        {t("phase4.xlsx.desc2", {
          fallback:
            "Export an eight-sheet workbook of operational practice data (Persons / Cases / Tasks / Events / Time entries / Invoices / Holidays, plus a manifest). Import is add-only — existing rows are skipped, new rows are created with fresh autoincrement IDs. Legal holidays are carried as rules, so adding a country is adding rows to the Holidays sheet.",
        })}
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className="btn btn-primary" onClick={download} disabled={busy}>
          <Download
            size={11}
            style={{ marginRight: 3, verticalAlign: "middle" }}
          />
          {t("phase4.xlsx.btn.download", { fallback: "Download XLSX" })}
        </button>
        <button
          className="btn btn-ghost"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          <Upload
            size={11}
            style={{ marginRight: 3, verticalAlign: "middle" }}
          />
          {busy && !preview
            ? t("phase4.xlsx.btn.previewing", { fallback: "Previewing…" })
            : t("phase4.xlsx.btn.upload", { fallback: "Upload XLSX" })}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) startPreview(f);
            e.target.value = "";
          }}
        />
      </div>

      {preview && (
        <ModalLayout
          title={t("phase4.xlsx.preview.title", { fallback: "Preview import" })}
          onClose={() => setPreview(null)}
          closeOnOverlay
          closeDisabled={busy}
          maxWidth={720}
          maxHeight="90vh"
          shellStyle={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
          rawBody
          footer={
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                padding: "10px 20px",
                borderTop: "1px solid var(--br)",
              }}
            >
              <button
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => setPreview(null)}
              >
                {t("common.cancel", { fallback: "Cancel" })}
              </button>
              <button
                className="btn btn-primary"
                disabled={busy || totalCreates === 0}
                onClick={confirmApply}
                title={
                  anyFkInvalid > 0
                    ? t("phase4.xlsx.preview.fkWarn", {
                        fallback:
                          "Some rows have invalid FKs and will be skipped",
                      })
                    : undefined
                }
              >
                {busy
                  ? t("phase4.xlsx.preview.applying", { fallback: "Applying…" })
                  : t("phase4.xlsx.preview.confirm", {
                      fallback: "Confirm & Apply",
                    })}
              </button>
            </div>
          }
        >
            <div style={{ padding: "14px 20px", overflowY: "auto", flex: 1 }}>
              <p
                style={{
                  fontSize: FS.s11,
                  color: "var(--tm)",
                  margin: "0 0 12px",
                  lineHeight: 1.5,
                }}
              >
                {t("phase4.xlsx.preview.note", {
                  fallback:
                    "Add-only import. Rows with an existing ID are skipped; rows with no ID or a new ID are inserted with a fresh autoincrement ID. FK references inside the upload resolve via an old→new ID map maintained during apply.",
                })}
              </p>
              {/* 59 §2 / 45a — THE SIX SHEET NAMES ARE NOT TRANSLATED, and the
                  workbook contract was checked before that was decided.
                  `server/src/routes/settingsXlsx.js:347` declares SHEETS by
                  these exact names and the IMPORT half looks each one up by
                  name — `wb.Sheets[sheetMeta.name]` (:619). Exit 23 of
                  `scripts/lint/i18n-exits.json` already rules them an
                  identifier plane ("an identifier is not a word", Laurent
                  2026-08-04) because the consumer and the breakage can both be
                  named. The literals below are not the sheet names themselves —
                  the identifier is the property key beside each one — but their
                  whole job is to tell an admin WHICH TAB of his own workbook a
                  count belongs to, and a heading that says "Personnes" over a
                  tab that says "Persons" breaks the only join he has. If this
                  preview is ever meant to be read without the workbook open,
                  exit 23's own answer applies: a separate human-facing surface,
                  not a renamed identifier. */}
              {renderSheetSection("Persons", preview.diff.Persons)}
              {renderSheetSection("Cases", preview.diff.Cases)}
              {renderSheetSection("Tasks", preview.diff.Tasks)}
              {renderSheetSection("Events", preview.diff.Events)}
              {renderSheetSection("TimeEntries", preview.diff.TimeEntries)}
              {renderSheetSection("Invoices", preview.diff.Invoices)}
              {/* 216 §3 (6) — the draws sheet (a workbook without it has no section: the server answers none) */}
              {preview.diff.PaymentDraws && renderSheetSection("PaymentDraws", preview.diff.PaymentDraws)}
              {/* 134 §3 — the eighth, under the same rule as the six above: the
                  heading is the TAB NAME, not a word, so it is not translated
                  (exit 23, "an identifier is not a word"). */}
              {renderSheetSection("Holidays", preview.diff.Holidays)}
              {totalCreates === 0 && (
                <p
                  style={{
                    fontSize: FS.s11,
                    color: "var(--am)",
                    margin: "8px 0 0",
                    fontStyle: "italic",
                  }}
                >
                  {t("phase4.xlsx.preview.noOp", {
                    fallback:
                      "Nothing to import — every row in this upload either already exists or fails FK validation.",
                  })}
                </p>
              )}
            </div>
        </ModalLayout>
      )}
    </>
  );
}
